// Voicemail backup / restore — the wire contract, in ONE place.
//
// Two pages drive these jobs: EasMigration.vue runs a backup before a
// migration, and VoicemailBackupLog.vue lists every job and starts restores.
// Both read the same /job/status body, so the field names live here rather
// than once per page — a rename on the backend is then a one-line change in
// this file, and cannot leave the two pages disagreeing about what a field is
// called.
//
// Nothing below imports Vue, the store or the service layer. It is pure data
// and pure functions, so it can be reasoned about without a component around
// it, and scripts/check-i18n-keys.js can read the i18n keys out of it.

// ---------------------------------------------------------------------------
// The wire names
// ---------------------------------------------------------------------------
//
// One row of /job/list and the Data of /job/status are the SAME shape, which
// is why one table serves both. Everything downstream reads the flat lowercase
// names normaliseVmJob() produces; no wire name may appear in a template, a
// computed property or a poll loop.
export const VM_FIELDS = {
  // Also read straight off the /backup/start and /restore/start envelopes,
  // where it sits at the top level rather than inside Data. Same spelling in
  // both places, so one entry covers both.
  jobId: "JobId",
  kind: "Kind",
  enterpriseId: "EnterpriseId",
  enterpriseName: "EnterpriseName",
  easName: "EasName",
  phase: "Phase",
  statusName: "StatusName",
  total: "TotalNumbers",
  done: "DoneNumbers",
  current: "CurrentNumber",
  messages: "MessageCount",
  errors: "ErrorCount",
  elapsed: "ElapsedSeconds",
  size: "SizeBytes",
  fileName: "FileName",
  errorLog: "ErrorLog",
  createdBy: "CreatedByName",
  createdAt: "CreatedAt",
  finishedAt: "FinishedAt",

  // Whether the zip is REALLY on the server's disk right now.
  //
  // The backend computes both of these by stat()ing the file, not from the
  // phase, and they are the only fields that can tell "ready" from "ready, but
  // the archive has since been reaped". A job stays ready in the database after
  // the retention sweep has deleted its zip, so `Phase === "ready"` alone
  // offers a download that answers 422 — and offering a download that fails is
  // worse than not offering one, on a feature whose whole purpose is proving a
  // copy exists before a migration destroys the originals.
  //
  // `Downloadable` is the fact as /job/status reports it; `HasFile` is the same
  // fact on a /job/list row. They are separate names on the wire, so they are
  // separate entries here.
  downloadable: "Downloadable",
  hasFile: "HasFile"
};

// One row of /job/log — the per-number record, and the only place an operator
// can find out WHICH numbers failed.
export const VM_LOG_FIELDS = {
  seq: "Seq",
  number: "Number",
  action: "Action",
  result: "Result",
  messages: "MessageCount",
  detail: "Detail",
  createdAt: "CreatedAt"
};

// ---------------------------------------------------------------------------
// Phases
// ---------------------------------------------------------------------------
//
// backup:   read -> compress -> ready
// restore:  extract -> restore -> done
// either:   error   (terminal)
//
// `ready` and `done` are the two terminal successes and are NOT interchangeable
// — `ready` means a zip exists to download, `done` means messages were written
// back to the switch and there is nothing to download at all.
export const VM_PHASE_READ = "read";
export const VM_PHASE_COMPRESS = "compress";
export const VM_PHASE_READY = "ready";
export const VM_PHASE_EXTRACT = "extract";
export const VM_PHASE_RESTORE = "restore";
export const VM_PHASE_DONE = "done";
export const VM_PHASE_ERROR = "error";

export const VM_KIND_BACKUP = "backup";
export const VM_KIND_RESTORE = "restore";

// ---------------------------------------------------------------------------
// The per-number Result — THREE values, and the third is the important one
// ---------------------------------------------------------------------------
//
//   ok       a messaging repository came back and was read. This INCLUDES a
//            genuinely empty mailbox: zero messages inside a repository that
//            was present is a complete, successful read of nothing.
//   error    the pull failed — a transport failure, or MetaSwitch refusing the
//            request with a non-SUCCESS result code.
//   no_data  MetaSwitch answered SUCCESS but the envelope carried no messaging
//            repository at all. That is NOT a read and NOT an empty mailbox:
//            an empty mailbox still returns its folder. It is what a number
//            resolved against the wrong subscriber looks like.
//
// This was two values until a blind test proved the collapse was silent data
// loss: a SUCCESS carrying no repository was recorded as a clean read of zero,
// ErrorCount stayed at nought, the modal said the archive was complete, and the
// operator authorised a migration that destroyed mailboxes the backup had never
// actually read.
//
// So `no_data` counts toward ErrorCount alongside `error` — but it is a
// DIFFERENT state and the UI must render it as one. Folding it into `error`
// hides that the switch answered fine; folding it into `ok` is the original
// bug. See resultBadge()/resultText() in VoicemailBackupLog.vue.
export const VM_RESULT_OK = "ok";
export const VM_RESULT_ERROR = "error";
export const VM_RESULT_NO_DATA = "no_data";

// ---------------------------------------------------------------------------
// One backend key, two honest meanings
// ---------------------------------------------------------------------------
//
// `errors.voicemailbackup.partial` is emitted by errorSummary() in
// controllers/WPVoiceMailBackupWorkerV4.go, which hangs off the run struct
// SHARED by both halves — so the same key ends a partial backup and a partial
// restore. The Go side's own detail text is neutral ("N of M number(s) did not
// complete successfully"), but a single translation of the key is not: a
// partial backup is a READ failure, and a partial restore is a WRITE failure
// with several distinct causes of its own — a number the switch refused, a
// message carrying no recording, a merge that added nothing.
//
// The backend cannot be asked to split the key (dtac-api-go is not ours to
// change here), and it does not need to be: the frontend always knows which
// half it is rendering. So the shared key is remapped by Kind at render time,
// and the backend key keeps a wording that is honest for either half in case it
// is ever rendered without one.
//
// Add an entry here, never a special case at a call site: this table is what
// keeps the two views saying the same thing about the same key.
const VM_ERROR_KEY_BY_KIND = {
  "errors.voicemailbackup.partial": {
    backup: "voicemaillog.partial_backup",
    restore: "voicemaillog.partial_restore"
  }
};

// Returns the key to translate for this Kind, or the key unchanged when there
// is no kind-specific wording (or no kind to go on).
export function vmErrorKeyForKind(key, kind) {
  const byKind = VM_ERROR_KEY_BY_KIND[key];
  if (!byKind) return key;
  return byKind[kind] || key;
}

// The single predicate for "this number was genuinely read", mirroring
// VmLogResultIsReadV4 in controllers/WPVoiceMailBackupStoreV4.go. Anything that
// is not a read counts as incomplete, and the bias is deliberately towards
// over-reporting: a false "check this" costs a minute, a false "all clear"
// costs a corporate's voicemail.
export function vmResultIsRead(result) {
  return result === VM_RESULT_OK;
}

// StatusName, as distinct from Phase. Only `failed` is consulted: a job may be
// marked failed by the worker's own supervisor without ever having written a
// Phase, and a poll loop that watched Phase alone would sit on it forever.
export const VM_STATUS_FAILED = "failed";

export const VM_POLL_MS = 2000;
export const VM_ZIP_MIME = "application/zip";

// ---------------------------------------------------------------------------
// Normalising
// ---------------------------------------------------------------------------

// A missing or non-numeric counter renders as 0 rather than NaN. Progress bars
// divide by `total`, and NaN% is worse than 0%.
export function vmNum(v) {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

function vmStr(v) {
  return v === null || v === undefined ? "" : String(v);
}

// The one place a /job/status Data — or a /job/list row — is turned into the
// flat shape the pages use. Called with null to reset between runs, so the
// empty state and the parsed state can never drift apart.
export function normaliseVmJob(raw) {
  const d = raw || {};
  return {
    jobId: vmNum(d[VM_FIELDS.jobId]),
    kind: vmStr(d[VM_FIELDS.kind]),
    enterpriseId: vmNum(d[VM_FIELDS.enterpriseId]),
    enterpriseName: vmStr(d[VM_FIELDS.enterpriseName]),
    easName: vmStr(d[VM_FIELDS.easName]),
    phase: vmStr(d[VM_FIELDS.phase]),
    statusName: vmStr(d[VM_FIELDS.statusName]),
    total: vmNum(d[VM_FIELDS.total]),
    done: vmNum(d[VM_FIELDS.done]),
    current: vmStr(d[VM_FIELDS.current]),
    messages: vmNum(d[VM_FIELDS.messages]),
    errors: vmNum(d[VM_FIELDS.errors]),
    elapsed: vmNum(d[VM_FIELDS.elapsed]),
    size: vmNum(d[VM_FIELDS.size]),
    fileName: vmStr(d[VM_FIELDS.fileName]),
    errorLog: vmStr(d[VM_FIELDS.errorLog]),
    createdBy: vmStr(d[VM_FIELDS.createdBy]),
    createdAt: vmStr(d[VM_FIELDS.createdAt]),
    finishedAt: vmStr(d[VM_FIELDS.finishedAt]),
    // Strictly `=== true` for both: an absent field must read as "there is no
    // file", so a backend that stopped sending it fails CLOSED — no download
    // offered — rather than offering one for an archive nobody has checked for.
    downloadable: d[VM_FIELDS.downloadable] === true,
    hasFile: d[VM_FIELDS.hasFile] === true
  };
}

export function normaliseVmLogRow(raw) {
  const d = raw || {};
  return {
    seq: vmNum(d[VM_LOG_FIELDS.seq]),
    number: vmStr(d[VM_LOG_FIELDS.number]),
    action: vmStr(d[VM_LOG_FIELDS.action]),
    result: vmStr(d[VM_LOG_FIELDS.result]),
    messages: vmNum(d[VM_LOG_FIELDS.messages]),
    detail: vmStr(d[VM_LOG_FIELDS.detail]),
    createdAt: vmStr(d[VM_LOG_FIELDS.createdAt])
  };
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export function formatVmDuration(seconds) {
  const s = Math.max(0, parseInt(seconds, 10) || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = n => String(n).padStart(2, "0");
  if (h > 0) return h + ":" + pad(m) + ":" + pad(sec);
  return pad(m) + ":" + pad(sec);
}

// 1024-based, one decimal from KB up. The zip is the thing the operator is
// waiting for, so its size is stated in a unit a human reads rather than in
// raw bytes.
export function formatVmBytes(bytes) {
  const n = Math.max(0, vmNum(bytes));
  if (n < 1024) return n + " B";
  const units = ["KB", "MB", "GB", "TB"];
  let value = n / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i++;
  }
  return value.toFixed(1) + " " + units[i];
}
